import {
  createEntityAdapter,
  createSlice,
  EntityState,
  PayloadAction,
  Update,
} from '@reduxjs/toolkit'
import { StoreData } from '../reducers/typings'
import {
  cancelForm,
  deleteForm,
  getAgreementConfigs,
  getAllTemplates,
  getForms,
  getGlobalTemplates,
  getTemplateById,
  getTemplates,
  reviseForm,
} from './api'
import {
  AgreementConfigs,
  FM_FORM_STATUSES,
  IForm,
  ITemplate,
  ITemplateDetail,
} from './typings'

const formAdapter = createEntityAdapter<IForm>()
const templateAdapter = createEntityAdapter<ITemplate>({
  selectId: (template) => template.templateId,
})

type AgreementsState = {
  forms: EntityState<IForm>
  templates: EntityState<ITemplate>
  selectedTemplate: SelectedTemplateState
  agreementConfigs: AgreementConfigs | null
}

type SelectedTemplateState = {
  isLoading: boolean
  template: ITemplateDetail | null
}

export const initialState: AgreementsState = {
  forms: formAdapter.getInitialState(),
  templates: templateAdapter.getInitialState(),
  selectedTemplate: {
    isLoading: false,
    template: null,
  },
  agreementConfigs: null,
}

export const agreementsSlice = createSlice({
  name: 'agreements',
  initialState,
  reducers: {
    updateFormStatus: (
      state,
      { payload }: PayloadAction<{ formId: string; status: FM_FORM_STATUSES }>,
    ) => {
      const update: Update<IForm> = {
        id: payload.formId,
        changes: {
          status: payload.status,
        },
      }
      formAdapter.updateOne(state.forms, update)
    },
    setSelectedTemplate: (
      state,
      { payload }: PayloadAction<SelectedTemplateState>,
    ) => {
      state.selectedTemplate = payload
    },
    clearAgreementConfigs: (state) => {
      state.agreementConfigs = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(cancelForm.fulfilled, (state, { payload }) => {
      const update: Update<IForm> = {
        id: payload.formId,
        changes: {
          status: FM_FORM_STATUSES.Canceled,
        },
      }
      formAdapter.updateOne(state.forms, update)
    }),
      builder.addCase(deleteForm.fulfilled, (state, { payload }) => {
        formAdapter.removeOne(state.forms, payload.formId)
      }),
      builder.addCase(getAllTemplates.fulfilled, (state, { payload }) => {
        templateAdapter.upsertMany(state.templates, payload)
      }),
      builder.addCase(getAllTemplates.pending, (state) => {
        templateAdapter.removeAll(state.templates)
      }),
      builder.addCase(getForms.fulfilled, (state, { payload }) => {
        formAdapter.upsertMany(state.forms, payload.items)
      }),
      builder.addCase(getForms.pending, (state) => {
        formAdapter.removeAll(state.forms)
      }),
      builder.addCase(getGlobalTemplates.fulfilled, (state, { payload }) => {
        templateAdapter.upsertMany(state.templates, payload)
      }),
      builder.addCase(getGlobalTemplates.pending, (state) => {
        templateAdapter.removeAll(state.templates)
      }),
      builder.addCase(getTemplates.fulfilled, (state, { payload }) => {
        templateAdapter.upsertMany(state.templates, payload)
      }),
      builder.addCase(getTemplates.pending, (state) => {
        templateAdapter.removeAll(state.templates)
      }),
      builder.addCase(getTemplateById.fulfilled, (state, { payload }) => {
        state.selectedTemplate = {
          isLoading: false,
          template: payload,
        }
      }),
      builder.addCase(getTemplateById.pending, (state) => {
        state.selectedTemplate = {
          isLoading: true,
          template: null,
        }
      })
    builder.addCase(reviseForm.fulfilled, (state, { payload }) => {
      const update: Update<IForm> = {
        id: payload.formId,
        changes: {
          status: FM_FORM_STATUSES.InProgress,
        },
      }
      formAdapter.updateOne(state.forms, update)
    })
    builder.addCase(getAgreementConfigs.fulfilled, (state, { payload }) => {
      state.agreementConfigs = payload
    })
  },
})

export default agreementsSlice.reducer

// TEMPLATE SELECTORS
export const agreementTemplateSelectors =
  templateAdapter.getSelectors<StoreData>((state) => state.agreements.templates)

export const {
  selectById: selectAgreementTemplateById,
  selectAll: selectAllAgreementTemplates,
} = agreementTemplateSelectors

export const selectSelectedTemplate = (
  state: StoreData,
): SelectedTemplateState => {
  return state.agreements.selectedTemplate
}

// FORM SELECTORS
export const agreementFormSelectors = formAdapter.getSelectors<StoreData>(
  (state) => state.agreements.forms,
)

// Agreement configs
export const selectAgreementConfigs = (state: StoreData) =>
  state.agreements.agreementConfigs

export const {
  selectById: selectAgreementFormById,
  selectAll: selectAllAgreementForms,
} = agreementFormSelectors

// AGREEMENT ACTIONS
export const { updateFormStatus, setSelectedTemplate, clearAgreementConfigs } =
  agreementsSlice.actions
